import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LISTADOS, columnasALiquidar } from './definiciones';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import ModalGestionarCarpeta from '../../components/Listados/ModalGestionarCarpeta';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import { getTramitesParaLiquidar, registrarControlLiquidar } from '../../services/tramiteService';
import { getTipoTramites } from '../../services/tipoTramiteService';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import FilaPlanilla from '../../components/Tramite/FilaPlanilla';
import { apellidoNombre } from '../../utils/nombrePersona';
import { toast } from 'react-toastify';
import PageHeader from '../../components/ui/PageHeader';

const DEF = LISTADOS.aLiquidar;

const CLAVE_MESES_ANTERIORES = 'meses-anteriores';

/** "2026-09" → "Septiembre 2026". */
function etiquetaDeMes(clave) {
    const fecha = new Date(`${clave}-01T00:00:00`);
    if (Number.isNaN(fecha.getTime())) return clave;
    const nombre = fecha.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

/**
 * En qué bloque cae cada carpeta.
 *
 * LA AGRUPACIÓN ES LA PLANILLA
 * La solapa LIQUIDAR se lee por mes ("junio 2026", "julio 2026", …), no fila por fila: la
 * pregunta siempre es "qué me toca este mes". Por eso los bloques son meses y no, por ejemplo,
 * tipos de trámite.
 *
 * TODO LO VENCIDO VA A UN SOLO BLOQUE
 * Los meses ya pasados se juntan en "Meses anteriores", que nace cerrado: lo que tiene que estar
 * a la vista al entrar es el mes corriente y el siguiente, no el arrastre. Pero adentro cada mes
 * se sigue viendo aparte (subEtiqueta), porque una carpeta que venció en abril y otra que venció
 * el mes pasado no son lo mismo y el orden entre ellas importa para decidir cuál reprogramar
 * primero. La fecha que se usa es la original, la que todavía nadie cambió.
 *
 * SIN FECHA ES SU PROPIO BLOQUE, NO EL MES CORRIENTE
 * Una carpeta sin fecha de liquidar no es "arranca ahora": es una a la que todavía nadie le
 * asignó mes. Meterla en el corriente haría que el mes de trabajo mezcle lo que hay que hacer con
 * lo que falta clasificar. Va al final, en un desplegable propio, que se va a ir vaciando solo:
 * desde que la atención cierra marcando "Inicia ahora" la carpeta nace con la fecha de hoy (ver
 * TurnoRecepcionService.asignarFechaLiquidarSiIniciaAhora), así que las que quedan acá son el
 * arrastre viejo.
 */
function grupoPorMes(row, mesCorriente) {
    const valor = row.fechaInicioEstimada;
    if (!valor) {
        return {
            clave: 'sin-fecha',
            etiqueta: 'Sin fecha asignada',
            // Desplegable pero abierto: hoy es donde está casi todo el listado, y arrancar con la
            // pantalla vacía sería peor que el problema que este bloque viene a resolver.
            colapsadoInicial: false,
        };
    }
    const clave = String(valor).slice(0, 7);

    if (clave < mesCorriente) {
        return {
            clave: CLAVE_MESES_ANTERIORES,
            etiqueta: 'Meses anteriores',
            colapsadoInicial: true,
            subEtiqueta: etiquetaDeMes(clave),
        };
    }
    // Abiertos de entrada solo el mes corriente y el siguiente: con la planilla migrada son más de
    // cien meses hacia adelante, y dibujarlos todos abiertos hacía la pantalla muy lenta. El botón
    // "Desplegar todos" de la tabla los abre de una.
    return { clave, etiqueta: etiquetaDeMes(clave), colapsadoInicial: clave > mesSiguiente(mesCorriente) };
}

/** "2026-12" → "2027-01". */
function mesSiguiente(clave) {
    const [anio, mes] = clave.split('-').map(Number);
    return mes === 12 ? `${anio + 1}-01` : `${anio}-${String(mes + 1).padStart(2, '0')}`;
}

/**
 * Primer tramo del circuito del estudio: **a liquidar** → turno ANSES → para aprobar → cobranzas.
 * Son las carpetas ya armadas que todavía no se presentaron.
 *
 * No es un listado de cobranzas: "liquidar" acá es el mes en que el cliente cumple los requisitos
 * y el trámite se puede iniciar. Clickear una fila va directo a la ficha del trámite; el botón
 * "Gestionar" de la última columna abre el control de la carpeta sin salir del listado, que es
 * como se trabaja (se controlan varias seguidas).
 */
export default function ListadoALiquidar() {
    const navigate = useNavigate();
    const [mes, setMes] = useFiltroPersistente('listadoALiquidar.mes', '');
    const [tipo, setTipo] = useFiltroPersistente('listadoALiquidar.tipo', '');
    // Revisión de la migración: solo las carpetas que vinieron de la planilla sin fecha vigente.
    const [revisarFecha, setRevisarFecha] = useFiltroPersistente('listadoALiquidar.revisarFecha', false);
    // Filas con la fila de Excel desplegada debajo.
    const [planillasAbiertas, setPlanillasAbiertas] = useState(() => new Set());
    const [tipos, setTipos] = useState([]);
    const [filaAGestionar, setFilaAGestionar] = useState(null);
    // Se incrementa al guardar el modal para que TablaListado vuelva a pedir la página: es una
    // dependencia más de su fetch, igual que los filtros.
    const [recarga, setRecarga] = useState(0);

    useEffect(() => {
        getTipoTramites()
            .then((datos) => setTipos(Array.isArray(datos) ? datos : []))
            .catch(() => setTipos([]));
    }, []);

    // useCallback y no una flecha inline: TablaListado la usa como dependencia de su useEffect, y
    // una función nueva en cada render dispara un pedido por render.
    const fetchPage = useCallback(
        (page, size, texto) => getTramitesParaLiquidar(mes, page, size, texto, tipo, revisarFecha),
        [mes, tipo, revisarFecha]
    );

    // El mes corriente se calcula una vez por render de la página y no dentro de grupoPorMes:
    // así todas las filas se comparan contra el mismo valor aunque la pestaña quede abierta
    // cruzando la medianoche del 1°.
    const mesCorriente = useMemo(() => new Date().toISOString().slice(0, 7), []);
    const agrupar = useCallback((row) => grupoPorMes(row, mesCorriente), [mesCorriente]);

    const verPlanilla = useCallback((row) => {
        setPlanillasAbiertas((previas) => {
            const nuevas = new Set(previas);
            if (nuevas.has(row.idTramite)) nuevas.delete(row.idTramite);
            else nuevas.add(row.idTramite);
            return nuevas;
        });
    }, []);

    // Asignar el mes desde la celda: pasa por el mismo registro de control que el modal, así queda
    // en el historial del trámite como cualquier otro cambio de fecha.
    const asignarMes = useCallback(async (row, mesElegido) => {
        try {
            await registrarControlLiquidar(row.idTramite, {
                marcarControlada: false,
                nota: 'Fecha asignada al revisar la carpeta migrada de la planilla',
                nuevaFechaInicio: `${mesElegido}-01`,
            });
            toast.success(`${apellidoNombre(row.apellidoCliente, row.nombreCliente)}: ${etiquetaDeMes(mesElegido)}.`);
            setRecarga((n) => n + 1);
        } catch (err) {
            toast.error(err.response?.data || 'No se pudo asignar la fecha.');
        }
    }, []);

    const columnas = useMemo(
        () => columnasALiquidar(setFilaAGestionar, { onAsignarMes: asignarMes, onVerPlanilla: verPlanilla, planillasAbiertas }),
        [asignarMes, verPlanilla, planillasAbiertas]
    );

    const filaDetalle = useCallback(
        (row) => (planillasAbiertas.has(row.idTramite) ? <FilaPlanilla idTramite={row.idTramite} /> : null),
        [planillasAbiertas]
    );

    const etiquetaMes = (valor) => (valor ? etiquetaDeMes(valor) : '');

    const chipsExtra = [
        mes && { key: 'mes', label: etiquetaMes(mes), onQuitar: () => setMes('') },
        tipo && { key: 'tipo', label: tipos.find((t) => String(t.idTipoTramite) === String(tipo))?.nombre || 'Tipo', onQuitar: () => setTipo('') },
        revisarFecha && { key: 'revisar', label: 'Revisar fecha', onQuitar: () => setRevisarFecha(false) },
    ].filter(Boolean);

    const limpiarExtra = () => {
        setMes('');
        setTipo('');
        setRevisarFecha(false);
    };

    return (
        <div className="container-fluid mt-4 listado-pagina area-cobranzas">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            <TablaListado
                claveFiltro="listadoALiquidar"
                columnas={columnas}
                fetchPage={fetchPage}
                onRowClick={(row) => navigate(rutaDetalleTramite(row))}
                rowKey={(row) => row.idTramite}
                dependencias={[mes, tipo, revisarFecha, recarga]}
                agruparPor={agrupar}
                filaDetalle={filaDetalle}
                compacto
                chipsExtra={chipsExtra}
                onLimpiarExtra={limpiarExtra}
                // Una sola página: la tabla agrupa por mes del lado del cliente, y paginar partiría un
                // mes entre dos páginas. Con la migración de la planilla son más de mil carpetas.
                size={2000}
                filtrosExtra={
                    <>
                        <div>
                            <label className="form-label" >
                                <i className="bi bi-calendar-event me-2"></i>Mes de inicio estimado
                            </label>
                            <input
                                type="month"
                                className="form-control"
                                value={mes}
                                onChange={(e) => setMes(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="form-label" >
                                <i className="bi bi-folder2-open me-2"></i>Tipo de trámite
                            </label>
                            <select className="form-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                                <option value="">Todos</option>
                                {tipos.map((t) => (
                                    <option key={t.idTipoTramite} value={t.idTipoTramite}>{t.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-end">
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input" type="checkbox" id="revisarFecha"
                                    checked={Boolean(revisarFecha)}
                                    onChange={(e) => setRevisarFecha(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="revisarFecha">
                                    Revisar fecha
                                </label>
                            </div>
                        </div>
                    </>
                }
            />

            <ModalGestionarCarpeta
                open={Boolean(filaAGestionar)}
                fila={filaAGestionar}
                onClose={() => setFilaAGestionar(null)}
                onGuardado={() => setRecarga((n) => n + 1)}
            />
        </div>
    );
}
